import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, Crown } from "lucide-react";
import GuildVault from "@/components/GuildVault";
import T from "@/components/T";
import GuildChat from "@/components/GuildChat";
import GuildContract from "@/components/GuildContract";
import GuildLogo, { GUILD_LOGO_KEYS } from "@/components/GuildLogo";
import GuildWars from "@/components/GuildWars";

export default function GuildsView({ ch, setCh }) {
  const [guilds, setGuilds] = useState([]);
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", tag: "", motto: "", logo: "raven", accent_color: "#E5C34A" });
  const ACCENT_PALETTE = ["#E5C34A", "#8A0303", "#4A7BC7", "#6E8B3D", "#8A2BE2", "#E55A00", "#B0ADA5", "#B44BE0"];
  // Public guild browser modal — guild_id to display, null to close.
  const [publicGid, setPublicGid] = useState(null);
  const [publicData, setPublicData] = useState(null);

  const refresh = async () => {
    setGuilds(await api.guilds());
    setMine(await api.guildMine());
  };
  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    if (!publicGid) { setPublicData(null); return; }
    api.guildPublic(publicGid).then(setPublicData).catch(() => setPublicData({ error: true }));
  }, [publicGid]);

  const create = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.guildCreateWithLogo(form.name, form.tag, form.motto, form.logo, form.accent_color);
      setCh(res.character);
      await refresh();
      toast.success("Guild founded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const join = async (gid) => {
    setBusy(true);
    try {
      const res = await api.guildJoin(gid);
      setCh(res.character);
      await refresh();
      toast.success("Welcome, brother/sister");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const leave = async () => {
    setBusy(true);
    try {
      const res = await api.guildLeave();
      setCh(res.character);
      await refresh();
      toast("You leave the ranks");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (mine) {
    return (
      <div className="space-y-2">
        <div className="panel p-3" data-testid="my-guild">
          <div className="flex items-start gap-2 mb-2">
            <Crown className="w-5 h-5 text-[#E5C34A] mt-1" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="font-heading text-lg text-[#E2DFD8]">
                «{mine.name}» <span className="text-[#8c8c8c] text-sm">[{mine.tag}]</span>
              </div>
              <div className="italic text-xs text-[#8c8c8c] mt-1">{mine.motto || "No motto set."}</div>
              <div className="font-mono text-[10px] text-[#B89C3A] mt-1">
                Level {mine.level} · {(mine.members || []).length} members · +{2 * mine.level} max energy while in guild
              </div>
            </div>
          </div>
          <div className="rune-div" />
          <div className="space-y-1">
            {(mine.members || []).map(m => (
              <div key={m.user_id} className="flex justify-between text-xs font-mono">
                <span className="text-[#E2DFD8]">{m.name}</span>
                <span className="text-[#8c8c8c] uppercase tracking-widest">{m.role}</span>
              </div>
            ))}
          </div>
          <button
            data-testid="guild-leave"
            onClick={leave}
            disabled={busy}
            className="mt-3 w-full py-2 border border-[#8A0303] text-[#E33333] font-heading text-xs tracking-widest uppercase"
          >
            Leave Guild
          </button>
        </div>
        <GuildContract isLeader={mine.leader_id === ch.user_id} />
        <GuildWars ch={ch} guilds={guilds} myGuild={mine} isLeader={mine.leader_id === ch.user_id} />
        <GuildVault guild={mine} ch={ch} setCh={setCh} refresh={refresh} />
        <GuildChat />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="panel p-3 space-y-2" data-testid="guild-create-form">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">
          <T>Found a Guild</T> <span className="text-[#B89C3A]">(500g)</span>
        </div>
        <input
          data-testid="guild-name"
          placeholder="Guild name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-2 text-sm text-[#E2DFD8]"
          maxLength={30}
        />
        <input
          data-testid="guild-tag"
          placeholder="Tag (max 5, e.g. GRIM)"
          value={form.tag}
          onChange={(e) => setForm({ ...form, tag: e.target.value.toUpperCase() })}
          className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-2 text-sm text-[#E2DFD8]"
          maxLength={5}
        />
        <input
          data-testid="guild-motto"
          placeholder="Motto (optional)"
          value={form.motto}
          onChange={(e) => setForm({ ...form, motto: e.target.value })}
          className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-2 text-sm text-[#E2DFD8]"
          maxLength={120}
        />
        {/* Logo picker — 10 preset dark-fantasy crests */}
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-[#8c8c8c] mb-1">
            Pick a Crest
          </div>
          <div className="grid grid-cols-5 gap-1" data-testid="guild-logo-picker">
            {GUILD_LOGO_KEYS.map(k => (
              <button
                key={k}
                data-testid={`guild-logo-${k}`}
                onClick={() => setForm({ ...form, logo: k })}
                className="aspect-square flex items-center justify-center border transition-colors"
                style={{
                  borderColor: form.logo === k ? form.accent_color : "#333",
                  background: form.logo === k ? `${form.accent_color}18` : "transparent",
                }}
                title={k}
              >
                <GuildLogo logo={k} size={30} color={form.logo === k ? form.accent_color : "#B0ADA5"} />
              </button>
            ))}
          </div>
        </div>
        {/* Accent color picker — tints the crest so guilds have unique feel */}
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-[#8c8c8c] mb-1">
            Accent Color
          </div>
          <div className="flex gap-1.5 flex-wrap" data-testid="guild-color-picker">
            {ACCENT_PALETTE.map(c => (
              <button
                key={c}
                data-testid={`guild-color-${c.slice(1)}`}
                onClick={() => setForm({ ...form, accent_color: c })}
                className="w-8 h-8 border-2 transition-transform"
                style={{
                  borderColor: form.accent_color === c ? "#E2DFD8" : "#333",
                  background: c,
                  transform: form.accent_color === c ? "scale(1.1)" : "scale(1)",
                }}
                title={c}
              />
            ))}
          </div>
        </div>
        <button
          data-testid="guild-create"
          onClick={create}
          disabled={busy || !form.name || !form.tag || ch.gold < 500}
          className="w-full py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-xs tracking-widest uppercase disabled:opacity-30"
        >
          Found (500g)
        </button>
      </div>

      <div className="panel p-2">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] px-2 py-1">
          Existing Guilds ({guilds.length})
        </div>
        {guilds.length === 0 && (
          <p className="italic text-[#666] text-xs px-2 py-2">No guilds have been founded yet.</p>
        )}
        {guilds.map(g => (
          <div key={g.guild_id} className="panel-inner p-2 mb-1 flex justify-between items-center gap-2" data-testid={`guild-${g.guild_id}`}>
            <button
              data-testid={`guild-view-${g.guild_id}`}
              onClick={() => setPublicGid(g.guild_id)}
              className="flex items-center gap-2 min-w-0 flex-1 text-left hover:opacity-80"
            >
              <GuildLogo logo={g.logo || "raven"} size={32} color={g.accent_color || "#B89C3A"} />
              <div className="min-w-0">
                <div className="font-heading text-sm text-[#E2DFD8] truncate">
                  «{g.name}» <span className="text-[#8c8c8c]">[{g.tag}]</span>
                </div>
                <div className="font-mono text-[10px] text-[#8c8c8c] flex items-center gap-1">
                  <Users className="w-3 h-3" strokeWidth={1.5} /> {g.member_count} · Lv {g.level}
                </div>
              </div>
            </button>
            <button
              data-testid={`guild-join-${g.guild_id}`}
              onClick={() => join(g.guild_id)}
              disabled={busy}
              className="px-3 py-1 border border-[#4A5D23] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase shrink-0"
            >
              Join
            </button>
          </div>
        ))}
      </div>

      {/* Public guild profile modal */}
      {publicGid && (
        <div
          className="fixed inset-0 z-[65] flex items-center justify-center px-3"
          style={{ background: "rgba(5,5,5,0.85)", backdropFilter: "blur(2px)" }}
          onClick={() => setPublicGid(null)}
          data-testid="guild-public-modal"
        >
          <div className="panel p-4 max-w-sm w-full space-y-3" onClick={(e) => e.stopPropagation()}>
            {!publicData && (
              <div className="text-center text-[#666] italic text-sm"><T>Loading…</T></div>
            )}
            {publicData?.error && (
              <div className="text-center text-[#8A0303] italic text-sm">Guild not found.</div>
            )}
            {publicData && !publicData.error && (
              <>
                <div className="flex items-start gap-3">
                  <GuildLogo logo={publicData.logo || "raven"} size={56} color={publicData.accent_color || "#E5C34A"} />
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-lg tracking-widest uppercase text-[#E2DFD8]">
                      «{publicData.name}»
                    </div>
                    <div className="font-mono text-[10px] tracking-widest uppercase text-[#B89C3A]">
                      [{publicData.tag}] · Lv {publicData.level} · {publicData.member_count} members
                    </div>
                    {publicData.motto && (
                      <div className="font-body italic text-[11px] text-[#8c8c8c] mt-1">
                        «{publicData.motto}»
                      </div>
                    )}
                  </div>
                </div>
                <div className="border-t border-[#262626] pt-2">
                  <div className="font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c] mb-1">
                    Roster
                  </div>
                  <div className="space-y-0.5 max-h-40 overflow-y-auto">
                    {publicData.members.map((m, i) => (
                      <div key={i} className="flex justify-between text-xs font-mono">
                        <span className="text-[#E2DFD8]">{m.name}</span>
                        <span className="text-[#8c8c8c] uppercase tracking-widest">{m.role}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => setPublicGid(null)}
                  className="w-full py-2 border border-[#333] text-[#8c8c8c] font-heading text-[10px] tracking-widest uppercase hover:border-[#E5C34A] hover:text-[#E5C34A]"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
